import {Color} from '@benchling/nucleus-ui';
import {Box, Group, Stack, Tabs, Text} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {getExtensionIcon} from 'dev-platform/extensionIconMapper';
import {ExtensionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications, {Response} from 'notifications';
import EntityStore from 'stores/EntityStore';
import VersionsTab from 'tenants/admin/extensions/VersionsTab';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as net from 'util/Net';

import {
  EXTENSION_TYPE_LABELS,
  registryLabelsForExtensionHeader,
  registryLabelsModelInputTypes,
  type Extension,
  type ExtensionLabelSpec,
} from './extensionConstants';
import {
  extensionWithInstallationIdFromInstallResponse,
  parseNonEmptyInstallationId,
  submitExtensionDirectoryInstall,
} from './extensionInstall';
import ExtensionTags, {RegistryLabelBadges} from './ExtensionTags';

export type ExtensionDetailsExtension = Omit<Extension, 'labels'> & Partial<Pick<Extension, 'labels'>>;
interface ExtensionDetailsProps {
  extension?: ExtensionDetailsExtension | null;
  isInstalled?: boolean;
  onInstall?: (extension: Extension) => void;
  onBeginInstallConfig?: () => void;
  labelSpecs: ReadonlyArray<ExtensionLabelSpec>;
  /** Which tab to show initially. Defaults to "details". */
  defaultTab?: 'details' | 'versions';
  /** Whether the "Set tag" dropdown is shown on the versions tab. */
  versionTagsEditable?: boolean;
}

export default function ExtensionDetails({
  extension,
  isInstalled,
  onInstall,
  onBeginInstallConfig,
  labelSpecs,
  defaultTab = 'details',
  versionTagsEditable = false,
}: ExtensionDetailsProps): JSX.Element {
  const [installing, setInstalling] = useState(false);
  const [installationId, setInstallationId] = useState<string | null>(extension?.installationId ?? null);

  useEffect(() => {
    setInstallationId(extension?.installationId ?? null);
  }, [extension?.id, extension?.installationId]);

  if (extension == null) {
    return (
      <Stack spacing="lg" p="md">
        <Text size="sm" c="dimmed">
          No details available.
        </Text>
      </Stack>
    );
  }

  const {provider, description} = extension;
  const displayName = extension.name ?? extension.id;
  const typeLabel = EXTENSION_TYPE_LABELS[extension.extensionType];

  const isTenantAdmin = EntityStore.currentUser()?.isTenantAdmin ?? false;
  const alreadyInstalled = isInstalled ?? installationId != null;
  const hasUrlParams = (extension.latestVersion?.mcpProperties?.urlParams?.length ?? 0) > 0;
  const needsApproval = extension.installApprovalRequired && extension.installApprovalText != null;
  const installDisabled = !isTenantAdmin || installing || needsApproval;

  const doInstall = async (): Promise<void> => {
    setInstalling(true);
    try {
      const response = await submitExtensionDirectoryInstall(extension as Extension);
      const merged = extensionWithInstallationIdFromInstallResponse(extension as Extension, response);
      const newInstallationId = parseNonEmptyInstallationId(merged.installationId);
      if (newInstallationId != null) {
        setInstallationId(newInstallationId);
      }
      notifications.success(`${displayName} installed successfully.`);

      onInstall?.(merged);
    } catch (err: unknown) {
      if (net.isServerRequestError(err)) {
        notifications.notifyError(err as Response);
      } else {
        throw err;
      }
    } finally {
      setInstalling(false);
    }
  };

  const handleInstallClick = (): void => {
    if (hasUrlParams && onBeginInstallConfig != null) {
      onBeginInstallConfig();
    } else {
      doInstall();
    }
  };

  return (
    <>
      {needsApproval && (
        <InlineBanner bannerType={InlineBannerType.WARNING} text={extension.installApprovalText} />
      )}
      <Stack spacing="lg" p="md">
        <Group align="flex-start" spacing="md">
          <Box className={css(extensionIconStyles.icon)}>
            {getExtensionIcon(extension.id, {
              className: css(extensionIconStyles.iconImg, extensionIconStyles.iconPlaceholder),
              extensionType: extension.extensionType,
            })}
          </Box>
          <Stack spacing="xs" style={{flex: 1, minWidth: 0}}>
            <Group spacing="xs" align="center" noWrap={true}>
              <Text fw={700} size="xl" component="span">
                {displayName}
              </Text>
              <Text size="sm" c="dimmed" component="span">
                by {provider.name}
              </Text>
            </Group>
            {extension.tagLine != null && extension.tagLine !== '' && (
              <Text size="md">{extension.tagLine}</Text>
            )}
            <Group spacing={4} mt="xs">
              <ExtensionTags
                extensionType={extension.extensionType}
                tags={extension.tags}
                registryLabels={registryLabelsForExtensionHeader(extension.labels, labelSpecs)}
              />
            </Group>
          </Stack>
          {alreadyInstalled || needsApproval ? (
            <Tooltip
              container="body"
              placement={TooltipPlacement.TOP}
              title={
                alreadyInstalled
                  ? 'Already installed on this tenant'
                  : `Approval is required before this ${typeLabel} can be installed`
              }
            >
              <span aria-disabled="true">
                <Button
                  text={alreadyInstalled ? 'Installed' : 'Install'}
                  buttonStyle={ButtonStyle.PRIMARY}
                  size={ButtonSize.MEDIUM}
                  disabled={true}
                />
              </span>
            </Tooltip>
          ) : (
            <Button
              text="Install"
              buttonStyle={ButtonStyle.PRIMARY}
              size={ButtonSize.MEDIUM}
              disabled={installDisabled}
              onClick={handleInstallClick}
            />
          )}
        </Group>

        {extension.extensionType === ExtensionType.MODEL ? (
          <Tabs defaultValue={defaultTab} styles={{tab: {fontSize: 14, fontWeight: 500}}} mt="sm">
            <Tabs.List>
              <Tabs.Tab value="details">Details</Tabs.Tab>
              <Tabs.Tab value="versions">Versions</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="details" pt="md">
              <DetailsTab description={description} />
            </Tabs.Panel>

            <Tabs.Panel value="versions" pt="md">
              <VersionsTab
                extensionDefinitionId={extension.id}
                extensionType={extension.extensionType}
                editable={versionTagsEditable}
              />
            </Tabs.Panel>
          </Tabs>
        ) : (
          <DetailsTab description={description} />
        )}
      </Stack>
    </>
  );
}

interface DetailsTabProps {
  description: Extension['description'];
}

function DetailsTab({description}: DetailsTabProps): JSX.Element {
  return (
    <Stack spacing="md" pt="lg">
      {description && (
        <Box>
          <Text size="md">{description}</Text>
        </Box>
      )}
    </Stack>
  );
}

interface ExtensionDetailsFooterProps {
  extension: ExtensionDetailsExtension;
}

export function ExtensionDetailsFooter({extension}: ExtensionDetailsFooterProps): JSX.Element {
  const {provider, links} = extension;
  const modelInputTypeLabels =
    extension.extensionType === ExtensionType.MODEL ? registryLabelsModelInputTypes(extension.labels) : [];

  const documentationLinks = links?.documentation ?? [];
  const policyLinks = links?.policy ?? [];
  const hasLinks = documentationLinks.length > 0 || policyLinks.length > 0;

  return (
    <Stack spacing="md" className={css(styles.stickyFooter)}>
      <Group align="flex-start" spacing={116} noWrap={false}>
        <Stack spacing="xs">
          <Text size="md" fw={600}>
            Provider
          </Text>
          <DetailValueLink label={provider.name} href={provider.url} />
        </Stack>
        {hasLinks && (
          <Stack spacing="xs">
            <Text size="md" fw={600}>
              Links
            </Text>
            <Stack spacing="sm">
              {documentationLinks.map((link) => (
                <DetailValueLink key={link.url} label={link.text ?? 'Documentation'} href={link.url} />
              ))}
              {policyLinks.map((link) => (
                <DetailValueLink key={link.url} label={link.text ?? 'Privacy Policy'} href={link.url} />
              ))}
            </Stack>
          </Stack>
        )}
        {modelInputTypeLabels.length > 0 && (
          <Stack spacing="xs">
            <Text size="md" fw={600}>
              Input types
            </Text>
            <Group spacing={4}>
              <RegistryLabelBadges labels={modelInputTypeLabels} />
            </Group>
          </Stack>
        )}
      </Group>
      {extension.isOwnedByBenchling && (
        <Text size="sm" c="dimmed" mt="lg">
          This {EXTENSION_TYPE_LABELS[extension.extensionType]} is managed and maintained by Benchling.
        </Text>
      )}
    </Stack>
  );
}

interface DetailValueLinkProps {
  label: string;
  href?: string | null;
}

function DetailValueLink({label, href}: DetailValueLinkProps): JSX.Element {
  if (href == null || href === '') {
    return (
      <Text size="sm" fw={500}>
        {label}
      </Text>
    );
  }

  return (
    <Text
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      size="sm"
      fw={500}
      className={css(styles.detailLink)}
    >
      {label}
    </Text>
  );
}

export const extensionIconStyles = StyleSheet.create({
  icon: {
    width: 48,
    height: 48,
    flexShrink: 0,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  iconImg: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  iconPlaceholder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    fontSize: 28,
    backgroundColor: Color.GRAY_100,
    borderRadius: 8,
  },
});

const styles = StyleSheet.create({
  stickyFooter: {
    borderTop: `1px solid ${Color.GRAY_200}`,
    paddingTop: 26,
    paddingBottom: 32,
  },
  detailLink: {
    color: Color.BLUE_450,
    textDecoration: 'underline',
    ':hover': {
      color: Color.BLUE_450,
    },
  },
});
